import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Web Speech API hook for TTS and STT.
 * Uses browser-native speech synthesis and recognition (free, no API key).
 *
 * Supported languages: he-IL (Hebrew), hi-IN (Hindi), en-US (English)
 *
 * Voice selection strategy:
 * 1. Prefer Google voices (higher quality neural TTS)
 * 2. Fallback to any voice matching the language
 * 3. Special handling for Hebrew — prefer female voices (clearer pronunciation)
 */

// Language code normalization
const LANG_MAP = {
  'he': 'he-IL',
  'he-IL': 'he-IL',
  'hi': 'hi-IN',
  'hi-IN': 'hi-IN',
  'en': 'en-US',
  'en-US': 'en-US',
  'en-GB': 'en-GB',
};

// Preferred voice names by language (ordered by preference)
const PREFERRED_VOICES = {
  'he-IL': [
    'Google עברית',           // Google Hebrew (Chrome)
    'Carmit',                 // macOS Hebrew
    'he-IL-Wavenet',          // If available
    'Microsoft Asaf',         // Windows Hebrew
  ],
  'hi-IN': [
    'Google हिन्दी',            // Google Hindi (Chrome)
    'Lekha',                  // macOS Hindi
    'hi-IN-Wavenet',
    'Microsoft Hemant',       // Windows Hindi
  ],
  'en-US': [
    'Google US English',      // Google (Chrome)
    'Samantha',               // macOS
    'Alex',                   // macOS
    'Microsoft David',        // Windows
    'Google UK English Female', // Fallback
  ],
};

// Speed adjustments per language (Hebrew/Hindi need slower for clarity)
const LANG_SPEED_FACTOR = {
  'he-IL': 0.85,  // Hebrew slightly slower for clearer pronunciation
  'hi-IN': 0.9,
  'en-US': 1.0,
  'en-GB': 1.0,
};

let cachedVoices = [];

function getVoices() {
  const voices = window.speechSynthesis?.getVoices() || [];
  if (voices.length > 0) cachedVoices = voices;
  return cachedVoices;
}

/**
 * Find the best voice for a given language code.
 */
function findBestVoice(langCode) {
  const voices = getVoices();
  if (voices.length === 0) return null;

  const normalizedLang = LANG_MAP[langCode] || langCode;
  const preferred = PREFERRED_VOICES[normalizedLang] || [];

  // 1. Try preferred voices by name (partial match)
  for (const prefName of preferred) {
    const match = voices.find(v =>
      v.name.includes(prefName) || v.voiceURI.includes(prefName)
    );
    if (match) return match;
  }

  // 2. Find any Google voice for this language
  const googleVoice = voices.find(v =>
    v.lang.startsWith(normalizedLang.split('-')[0]) &&
    v.name.toLowerCase().includes('google')
  );
  if (googleVoice) return googleVoice;

  // 3. Find any "enhanced" or "premium" voice
  const enhancedVoice = voices.find(v =>
    v.lang.startsWith(normalizedLang.split('-')[0]) &&
    (v.name.toLowerCase().includes('enhanced') ||
     v.name.toLowerCase().includes('premium') ||
     v.name.toLowerCase().includes('natural'))
  );
  if (enhancedVoice) return enhancedVoice;

  // 4. Find any voice matching the full locale
  const localeMatch = voices.find(v => v.lang === normalizedLang);
  if (localeMatch) return localeMatch;

  // 5. Find any voice matching the base language
  const baseLang = normalizedLang.split('-')[0];
  const baseMatch = voices.find(v => v.lang.startsWith(baseLang));
  if (baseMatch) return baseMatch;

  return null;
}

export default function useVoice() {
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);
  const voicesLoadedRef = useRef(false);

  // Pre-load voices (they load asynchronously in Chrome)
  useEffect(() => {
    if (!window.speechSynthesis) return;

    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        cachedVoices = voices;
        voicesLoadedRef.current = true;
        console.log(`[useVoice] Loaded ${voices.length} voices`);
        // Log available voices for debugging
        const heb = voices.filter(v => v.lang.startsWith('he'));
        const hin = voices.filter(v => v.lang.startsWith('hi'));
        const eng = voices.filter(v => v.lang.startsWith('en'));
        console.log(`[useVoice] Hebrew voices: ${heb.map(v => v.name).join(', ') || 'none'}`);
        console.log(`[useVoice] Hindi voices: ${hin.map(v => v.name).join(', ') || 'none'}`);
        console.log(`[useVoice] English voices (first 5): ${eng.slice(0, 5).map(v => v.name).join(', ')}`);
      }
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  // Text-to-Speech with smart voice selection
  const speak = useCallback((text, lang = 'en-US', speed = 1.0) => {
    if (!window.speechSynthesis) {
      console.warn('Speech synthesis not supported');
      return;
    }

    // Cancel any current speech
    window.speechSynthesis.cancel();

    const normalizedLang = LANG_MAP[lang] || lang;
    const utterance = new SpeechSynthesisUtterance(text);

    // Find best voice
    const voice = findBestVoice(normalizedLang);
    if (voice) {
      utterance.voice = voice;
      utterance.lang = voice.lang; // Use the voice's actual lang tag
      console.log(`[useVoice] Using voice: "${voice.name}" (${voice.lang})`);
    } else {
      utterance.lang = normalizedLang;
      console.log(`[useVoice] No specific voice found for ${normalizedLang}, using default`);
    }

    // Adjust speed: apply language factor, then user speed
    const langFactor = LANG_SPEED_FACTOR[normalizedLang] || 1.0;
    utterance.rate = speed * langFactor;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = (e) => {
      console.error('[useVoice] Speech error:', e.error);
      setSpeaking(false);
    };

    // Chrome bug workaround: speech can stop after ~15 seconds
    // Resume periodically for long texts
    const resumeInterval = setInterval(() => {
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.pause();
        window.speechSynthesis.resume();
      } else {
        clearInterval(resumeInterval);
      }
    }, 10000);

    utterance.onend = () => {
      clearInterval(resumeInterval);
      setSpeaking(false);
    };
    utterance.onerror = (e) => {
      clearInterval(resumeInterval);
      console.error('[useVoice] Speech error:', e.error);
      setSpeaking(false);
    };

    window.speechSynthesis.speak(utterance);
  }, []);

  // Stop speaking
  const stopSpeaking = useCallback(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    }
  }, []);

  // Speech-to-Text
  const listen = useCallback((lang = 'en-US', onResult) => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.warn('Speech recognition not supported');
      return;
    }

    // Stop any existing recognition
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }

    const normalizedLang = LANG_MAP[lang] || lang;
    const recognition = new SpeechRecognition();
    recognition.lang = normalizedLang;
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => setListening(true);

    recognition.onresult = (event) => {
      const text = event.results[0][0].transcript;
      setTranscript(text);
      if (onResult) onResult(text);
    };

    recognition.onend = () => setListening(false);
    recognition.onerror = (e) => {
      console.error('[useVoice] Recognition error:', e.error);
      setListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, []);

  // Stop listening
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setListening(false);
    }
  }, []);

  // Get available voices for a language (useful for debugging/settings)
  const getAvailableVoices = useCallback((lang) => {
    const normalizedLang = LANG_MAP[lang] || lang;
    const baseLang = normalizedLang.split('-')[0];
    return getVoices().filter(v => v.lang.startsWith(baseLang));
  }, []);

  return {
    speak,
    stopSpeaking,
    speaking,
    listen,
    stopListening,
    listening,
    transcript,
    getAvailableVoices,
  };
}
